/**
 * GoalDetailPanel — goal detail body, rendered inside GoalDetailDialog (the
 * centered modal pop-up). Originally an inline row-expansion (Story 18.8 AC#9);
 * the surface moved to a §4.1 centered modal, but this component is unchanged
 * except for shedding its own outer padding (the modal's p-8 supplies it).
 *
 * Shows the full pacing surface from progress (services/goal-progress.ts),
 * period bounds (UTC, works for rolling AND custom periods), days
 * elapsed/remaining, created date, and the edit/pause/delete actions. Budget
 * goals get inverted pacing copy.
 *
 * Historical snapshots are a SEAM ONLY (the 18.7 slot below) — this story does
 * NOT fetch or chart snapshots.
 */

import { Button } from "@provenleap/ui";
import { PencilSimple, Pause, Play, Trash } from "@phosphor-icons/react";
import {
  displayStatus,
  GOAL_STATUS_BADGES,
  type GoalListItem,
} from "../../lib/goalPageDisplay";
import { formatGoalValue, clampBarFill } from "../../lib/goalFormat";
import { GoalHistoryChart } from "./GoalHistoryChart";

interface GoalDetailPanelProps {
  goal: GoalListItem;
  canManage: boolean;
  onEdit: () => void;
  onTogglePause: () => void;
  onDelete: () => void;
}

function fmtUtcDate(ms: number): string {
  return new Date(ms).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });
}

export function GoalDetailPanel({
  goal,
  canManage,
  onEdit,
  onTogglePause,
  onDelete,
}: GoalDetailPanelProps) {
  const status = displayStatus(goal);
  const badge = GOAL_STATUS_BADGES[status];
  const p = goal.progress;
  const fill = clampBarFill(p.percentage);
  // Expected-pace tick position along the bar (linear expected / target).
  const expectedTick =
    goal.targetValue !== 0
      ? clampBarFill((p.expectedValue / goal.targetValue) * 100)
      : 0;
  const isBudget = goal.goalType === "budget";
  const periodEnded = p.daysRemaining <= 0;

  const fmt = (v: number) => formatGoalValue(v, goal.unit, goal.currencyCode);

  // Pacing projection copy — budget inverts the framing (spend vs budget).
  const projectionCopy = isBudget
    ? `Projected spend ${fmt(p.projectedValue)} of budget ${fmt(goal.targetValue)}`
    : `Projected ${fmt(p.projectedValue)} by end of period`;

  return (
    <div className="space-y-5">
      {/* Full-width progress bar with an expected-pace target tick */}
      <div className="space-y-1.5">
        <div className="relative h-2.5 w-full overflow-hidden rounded-full bg-muted">
          <div
            className={`h-full rounded-full ${badge.bar}`}
            style={{ width: `${fill}%` }}
          />
          {/* Expected-pace tick (the GoalWidget target-tick semantics). */}
          <div
            className="absolute top-0 h-full w-0.5 bg-foreground/40"
            style={{ left: `${expectedTick}%` }}
            title="Expected pace"
            aria-hidden="true"
          />
        </div>
        <div className="flex items-center justify-between text-[12px]">
          <span className="text-muted-foreground">
            {fmt(goal.currentValue)} of {fmt(goal.targetValue)}
          </span>
          <span className="font-semibold tabular-nums text-foreground">
            {Math.round(p.percentage)}%
          </span>
        </div>
      </div>

      {/* Pacing projection + expected-vs-current */}
      <div className="grid gap-3 sm:grid-cols-2">
        <DetailStat label="Pacing" value={projectionCopy} />
        <DetailStat
          label={isBudget ? "Budget pace" : "Expected vs current"}
          value={`Expected ${fmt(p.expectedValue)} · Current ${fmt(goal.currentValue)}`}
        />
        <DetailStat
          label="Period"
          value={`${fmtUtcDate(p.periodStart)} – ${fmtUtcDate(p.periodEnd)}`}
        />
        <DetailStat
          label="Timeline"
          value={
            periodEnded
              ? `${p.daysElapsed} of ${p.daysTotal} days elapsed (period ended)`
              : `${p.daysElapsed} of ${p.daysTotal} days elapsed · ${p.daysRemaining} remaining`
          }
        />
        {goal.createdAt !== null && (
          <DetailStat label="Created" value={fmtUtcDate(goal.createdAt)} />
        )}
        {isBudget && (
          <DetailStat
            label="Pacing rule"
            value="On track = projected spend within budget"
          />
        )}
      </div>

      {/* Story 18.7: snapshot history chart — self-contained (own hook call). */}
      <GoalHistoryChart goalId={goal.id} />

      {canManage && (
        <div className="flex items-center gap-2 pt-1">
          <Button
            variant="outline"
            size="sm"
            onClick={onEdit}
            className="rounded-2xl text-[13px] shadow-xs cursor-pointer hover:bg-background hover:-translate-y-0.5 hover:shadow-sm transition-all"
            style={{ border: "0.5px solid var(--hairline)" }}
          >
            <PencilSimple size={14} className="mr-1" />
            Edit
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={onTogglePause}
            className="rounded-2xl text-[13px] shadow-xs cursor-pointer hover:bg-background hover:-translate-y-0.5 hover:shadow-sm transition-all"
            style={{ border: "0.5px solid var(--hairline)" }}
          >
            {goal.status === "paused" ? (
              <>
                <Play size={14} className="mr-1" />
                Resume
              </>
            ) : (
              <>
                <Pause size={14} className="mr-1" />
                Pause
              </>
            )}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={onDelete}
            className="rounded-2xl text-[13px] text-destructive cursor-pointer hover:bg-destructive/10 transition-all"
          >
            <Trash size={14} className="mr-1" />
            Delete
          </Button>
        </div>
      )}
    </div>
  );
}

function DetailStat({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col">
      <span className="text-[11px] uppercase tracking-wide text-muted-foreground">
        {label}
      </span>
      <span className="text-[13px] text-foreground">{value}</span>
    </div>
  );
}
